import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  UserMinus, ListChecks, CheckCircle2, Circle, Lock, RefreshCw,
  AlertTriangle, ArrowRight
} from 'lucide-react';

export default function OffboardingPage() {
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [openId, setOpenId] = useState(searchParams.get('id') || null);
  const [active, setActive] = useState(null);
  const [openInit, setOpenInit] = useState({ show: false, employee_id: '' });

  const load = useCallback(async () => {
    try {
      const r = await apiClient.get('/hr/offboarding');
      setItems(r.data?.offboardings || []);
    } catch {/* */ }
  }, []);
  const loadEmployees = useCallback(async () => {
    try {
      const r = await apiClient.get('/hr/employees?status=active&limit=500');
      const list = Array.isArray(r.data) ? r.data : (r.data?.employees || []);
      setEmployees(list.filter(e => (e.employment_status || 'active') === 'active'));
    } catch {/* */ }
  }, []);
  const loadDetail = useCallback(async (employeeId) => {
    try {
      const r = await apiClient.get(`/hr/offboarding/${employeeId}`);
      setActive(r.data?.checklist);
    } catch {/* */ }
  }, []);

  useEffect(() => { load(); loadEmployees(); }, [load, loadEmployees]);
  useEffect(() => { if (openId) loadDetail(openId); }, [openId, loadDetail]);

  async function startNew() {
    if (!openInit.employee_id) return toast.error('Pick employee');
    try {
      const r = await apiClient.post(`/hr/offboarding/${openInit.employee_id}`);
      toast.success('Offboarding initiated');
      setActive(r.data);
      setOpenId(openInit.employee_id);
      setOpenInit({ show: false, employee_id: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Failed');
    }
  }
  async function tick(itemKey, done) {
    try {
      await apiClient.put(`/hr/offboarding/${openId}/items/${itemKey}`, { done });
      loadDetail(openId); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  }
  async function complete() {
    if (!window.confirm('Mark offboarding complete? This cannot be undone.')) return;
    try {
      await apiClient.post(`/hr/offboarding/${openId}/complete`);
      toast.success('Offboarding completed');
      loadDetail(openId); load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.incomplete) {
        toast.error(`Pending: ${detail.incomplete.join(', ')}`);
      } else {
        toast.error(detail?.message || detail || 'Failed');
      }
    }
  }

  return (
    <div className="space-y-6" data-testid="offboarding-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-rose-500 to-red-600 flex items-center justify-center">
            <UserMinus className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Employee Offboarding</h1>
            <p className="text-muted-foreground text-sm">Tracked checklist for every termination. Termination cannot complete until required items are ticked.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-1.5" />Refresh</Button>
          <Button onClick={() => setOpenInit({ show: true, employee_id: '' })} data-testid="init-offboarding-btn">
            <UserMinus className="h-4 w-4 mr-1.5" /> Initiate Offboarding
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2"><CardTitle className="text-base">Active Offboardings</CardTitle></CardHeader>
          <CardContent className="space-y-2 max-h-[70vh] overflow-y-auto">
            {items.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">No active offboardings.</p>}
            {items.map(o => (
              <button key={o.id} onClick={() => setOpenId(o.employee_id)} className={`w-full text-left p-3 rounded-lg border transition ${openId === o.employee_id ? 'border-primary bg-primary/5' : 'hover:border-primary/40'}`} data-testid={`offboarding-row-${o.employee_id}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="font-medium text-sm">{o.employee_name}</p>
                  <Badge variant="outline" className={
                    o.status === 'completed' ? 'bg-emerald-500/10 text-emerald-700'
                    : o.status === 'ready_to_close' ? 'bg-blue-500/10 text-blue-700'
                    : 'bg-amber-500/10 text-amber-700'}>
                    {o.status?.replace('_', ' ')}
                  </Badge>
                </div>
                <Progress value={o.progress?.pct || 0} className="h-1.5" />
                <p className="text-xs text-muted-foreground mt-1">{o.progress?.done}/{o.progress?.total} items done</p>
              </button>
            ))}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            {active ? (
              <>
                <CardTitle className="flex items-center gap-2">
                  {active.employee_name}
                  <Badge variant="outline">{active.status}</Badge>
                </CardTitle>
                <CardDescription>
                  Opened {active.opened_at?.slice(0, 10)} by {active.opened_by_name}
                </CardDescription>
              </>
            ) : (
              <CardTitle className="text-muted-foreground">Pick an offboarding from the list</CardTitle>
            )}
          </CardHeader>
          {active && (
            <CardContent className="space-y-3">
              {active.status !== 'completed' && active.items.every(i => i.done || !i.required) && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-3 text-sm flex items-center justify-between">
                  <span className="text-emerald-700 font-medium">All required items ticked. Ready to close.</span>
                  <Button size="sm" onClick={complete} data-testid="complete-offboarding-btn">
                    <Lock className="h-4 w-4 mr-1.5" /> Mark Completed
                  </Button>
                </div>
              )}
              <div className="space-y-1.5">
                {active.items.map(it => (
                  <div key={it.key} className={`flex items-start gap-2 p-3 border rounded-lg ${it.done ? 'bg-emerald-500/5 border-emerald-500/30' : ''}`} data-testid={`item-${it.key}`}>
                    <button
                      disabled={it.auto_check || active.status === 'completed'}
                      onClick={() => tick(it.key, !it.done)}
                      className="mt-0.5"
                      title={it.auto_check ? 'Auto-checked when the underlying condition is met' : ''}
                      data-testid={`tick-${it.key}`}
                    >
                      {it.done ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Circle className="h-5 w-5 text-muted-foreground" />}
                    </button>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">
                        {it.label}
                        {!it.required && <span className="ml-1 text-xs text-muted-foreground">(optional)</span>}
                        {it.auto_check && <span className="ml-1 text-xs text-blue-600">(auto)</span>}
                      </p>
                      {it.done && (
                        <p className="text-xs text-muted-foreground">
                          ✓ {it.done_by_name || 'System'} · {it.done_at?.slice(0, 16).replace('T', ' ')}
                        </p>
                      )}
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Owner: {it.owner_role}</p>
                    </div>
                  </div>
                ))}
              </div>
              {active.history?.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">History ({active.history.length})</summary>
                  <ul className="mt-2 space-y-1 ml-4">
                    {active.history.map((h, i) => (
                      <li key={i}>
                        <code>{h.at?.slice(0, 16).replace('T', ' ')}</code> — {h.action} by {h.by_name || 'system'}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </CardContent>
          )}
        </Card>
      </div>

      <Dialog open={openInit.show} onOpenChange={o => !o && setOpenInit({ show: false, employee_id: '' })}>
        <DialogContent className="max-w-md" data-testid="init-modal">
          <DialogHeader><DialogTitle>Initiate Offboarding</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3 text-xs text-amber-800 dark:text-amber-300 flex gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>This will open the checklist and notify the employee. Existing in-progress offboardings will be reused.</span>
            </div>
            <Select value={openInit.employee_id} onValueChange={v => setOpenInit({ ...openInit, employee_id: v })}>
              <SelectTrigger data-testid="init-employee"><SelectValue placeholder="Pick employee" /></SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name} {e.employee_id ? `(${e.employee_id})` : ''}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpenInit({ show: false, employee_id: '' })}>Cancel</Button><Button onClick={startNew} data-testid="confirm-init"><ArrowRight className="h-4 w-4 mr-1.5" />Start</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
